import React, { useEffect, useState } from 'react';
import axiosInstance from '../Core/RestHelper';
import { PlansPage } from './BillingView';
import { GrantAwsAccessModal } from '../Components/GrantAwsAccessModal';
import { InviteMemberModal } from '../Components/InviteMemeberModalProps';
import { TeamMember, PendingInvite } from '../Model/Teams';

import { useNavigate } from 'react-router-dom';
import { Github } from 'lucide-react';
import { FaBitbucket, FaGitlab } from 'react-icons/fa';
import { BillingFormModal } from '../Components/BillingFormModal';

// react-icons v5 has a type mismatch with React 18's JSX types — cast to fix
const BitbucketIcon = FaBitbucket as unknown as React.FC<{ size?: number; color?: string; style?: React.CSSProperties }>;
const GitlabIcon = FaGitlab as unknown as React.FC<{ size?: number; color?: string; style?: React.CSSProperties }>;


interface Plan {
  plan_name: string;
  active:boolean;
  deployments_allowed_per_month: string;
  perks: {
    cloud_providers: string;
    deployment_targets: string;
    monitoring: boolean;
    infra_management: boolean;
    alerts: boolean;
    team_based_access: boolean;
    email_response_time: number;
    support?:string;
  };
  rz_plan_id: string;
  plan_id: number;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: '#111116',
  border: '1px solid #26262f',
  borderRadius: '10px',
  padding: '18px',
};

const btn = (variant: 'primary' | 'secondary' | 'ghost' = 'secondary'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '7px 14px', borderRadius: '6px', fontSize: '13px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s',
  ...(variant === 'primary'
    ? { background: '#6366f1', color: '#fff' }
    : variant === 'ghost'
    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid transparent' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

const perkRow = (enabled: boolean, label: string) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: enabled ? '#eeeef5' : '#52525f' }}>
    {enabled ? (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M2 6.5l4 4 5-5"/></svg>
    ) : (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#52525f" strokeWidth="1.5"><path d="M3 3l7 7M10 3L3 10"/></svg>
    )}
    {label}
  </div>
);

const formatPlanName = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

const planAccentColor = (name: string) => {
  switch (name.toLowerCase()) {
    case 'free': return { color: '#8e8ea8', bg: '#1e1e27', border: '#32323d' };
    case 'starter': return { color: '#3b82f6', bg: 'rgba(59,130,246,.1)', border: 'rgba(59,130,246,.2)' };
    case 'professional': return { color: '#818cf8', bg: 'rgba(99,102,241,.14)', border: 'rgba(99,102,241,.25)' };
    case 'enterprise': return { color: '#22c55e', bg: 'rgba(34,197,94,.1)', border: 'rgba(34,197,94,.2)' };
    default: return { color: '#8e8ea8', bg: '#1e1e27', border: '#32323d' };
  }
};

// ── ProfileView ───────────────────────────────────────────────────────────────

export const ProfileView: React.FC = () => {
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [allPlans, setAllPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [username, setUserName] = useState<string>();
  const [userEmail, setUserEmail] = useState<string>();
  const navigate = useNavigate();

  // ── Team management state ──────────────────────────────────────────────────
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [seatsAllowed, setSeatsAllowed] = useState<string>('1');
  const [isTeamLoading, setIsTeamLoading] = useState(true);
  const [isOrgOwner, setIsOrgOwner] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [accessModalMember, setAccessModalMember] = useState<TeamMember | null>(null);
  const [pendingActionId, setPendingActionId] = useState<number | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<number | null>(null);

  useEffect(() => {
    fetchPlans();
    fetchCurrentPlan();
    fetchTeamData();
  }, []);

  const fetchCurrentPlan = async () => {
    try {
      const res = await axiosInstance.get('/users/dashboard');
      setCurrentPlan(res.data.plan_details);
      setUserEmail(res.data.email);
      setUserName(res.data.username);
    } catch (error) {
      console.error('Error fetching current plan:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axiosInstance.get('/admin/plans/');
      setAllPlans(res.data.filter((p: Plan) => p.active));
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // owner-only endpoints — a team member hitting these gets a 403, which we
  // use as the signal to render the simplified, no-management view instead
  const fetchTeamData = async () => {
    try {
      setIsTeamLoading(true);
      const [membersRes, invitesRes] = await Promise.all([
        axiosInstance.get('/teams/members'),
        axiosInstance.get('/teams/invites'),
      ]);
      setIsOrgOwner(membersRes.data.is_owner);
      setTeamMembers(membersRes.data.members);
      setSeatsUsed(membersRes.data.seats_used);
      setSeatsAllowed(membersRes.data.seats_allowed);
      setPendingInvites(invitesRes.data.invites);
    } catch (error: any) {
      console.error('Error fetching team data:', error);
      setTeamError('Could not load team information.');
    } finally {
      setIsTeamLoading(false);
    }
  };

  const toggleCanAddConnections = async (member: TeamMember) => {
    if (pendingActionId !== null) return;
    setPendingActionId(member.id);
    const next = !member.can_add_connections;
    setTeamMembers(prev => prev.map(m => (m.id === member.id ? { ...m, can_add_connections: next } : m)));
    try {
      await axiosInstance.patch(`/teams/members/${member.id}`, { can_add_connections: next });
    } catch (error) {
      setTeamMembers(prev => prev.map(m => (m.id === member.id ? { ...m, can_add_connections: !next } : m)));
      setTeamError('Could not update permission. Try again.');
    } finally {
      setPendingActionId(null);
    }
  };

  const removeMember = async (member: TeamMember) => {
    if (!window.confirm(`Remove ${member.username} from the team? They'll lose access to all shared connections.`)) {
      return;
    }
    setPendingActionId(member.id);
    try {
      await axiosInstance.delete(`/teams/members/${member.id}`);
      setTeamMembers(prev => prev.filter(m => m.id !== member.id));
      setSeatsUsed(prev => Math.max(0, prev - 1));
    } catch (error) {
      setTeamError('Could not remove team member. Try again.');
    } finally {
      setPendingActionId(null);
    }
  };

  const revokeInvite = async (invite: PendingInvite) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    setPendingActionId(invite.id);
    try {
      await axiosInstance.delete(`/teams/invites/${invite.id}`);
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch (error) {
      setTeamError('Could not revoke invite. Try again.');
    } finally {
      setPendingActionId(null);
    }
  };

  const copyInviteLink = async (invite: PendingInvite) => {
    await navigator.clipboard.writeText(invite.invite_link);
    setCopiedInviteId(invite.id);
    setTimeout(() => setCopiedInviteId(null), 1500);
  };

  const handleUpgrade = (plan: Plan) => {
    if (plan.plan_id === currentPlan?.plan_id) return;
    setSelectedPlan(plan);
    setShowBillingModal(true);
  };

  const handleCloseBillingModal = () => {
    setShowBillingModal(false);
    setSelectedPlan(null);
  };

  if (isLoading) {
    return (
      <div style={{ background: '#09090e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }}/>
        <span style={{ fontSize: '13px', color: '#52525f' }}>Loading profile…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const accentColor = planAccentColor(currentPlan?.plan_name || '');
  const initials = username ? username.charAt(0).toUpperCase() : 'U';

  const seatsAllowedLabel = seatsAllowed === 'unlimited' ? 'Unlimited' : seatsAllowed;
  const seatsAllowedNum = seatsAllowed === 'unlimited' ? Infinity : parseInt(seatsAllowed, 10) || 1;
  const seatsCombinedUsed = seatsUsed + pendingInvites.length; // pending invites count against the gate too
  const seatsFull = seatsCombinedUsed >= seatsAllowedNum;
  const seatsPct = seatsAllowed === 'unlimited' ? 0 : Math.min(100, (seatsCombinedUsed / seatsAllowedNum) * 100);

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Topbar */}
      <div style={{ height: '50px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky' as const, top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span>Home</span><span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>Profile</span>
        </div>
      </div>

      <div style={{ padding: '20px 24px', maxWidth: '900px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>Profile</h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>Manage your account and subscription</p>
        </div>

        {/* Profile card */}
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '50%',
            background: 'linear-gradient(135deg,#6366f1,#a855f7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#eeeef5' }}>{username || '—'}</div>
            <div style={{ fontSize: '13px', color: '#8e8ea8', marginTop: '2px' }}>{userEmail || '—'}</div>
          </div>
          <span style={{
            display: 'inline-flex', padding: '3px 10px', borderRadius: '20px',
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.05em',
            background: accentColor.bg, color: accentColor.color, border: `1px solid ${accentColor.border}`,
          }}>
            {formatPlanName(currentPlan?.plan_name || 'Free')}
          </span>
        </div>

        {/* Current plan stats */}



        {/* Team section — visible to all, actions gated to owners only */}
        <div style={{ ...card, marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5' }}>Team</div>
                {!isOrgOwner && (
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#8e8ea8', background: '#1e1e27', border: '1px solid #32323d', borderRadius: '4px', padding: '2px 6px', textTransform: 'uppercase' as const, letterSpacing: '.04em' }}>
                    Read only
                  </span>
                )}
              </div>
              {isOrgOwner && (
                <div style={{ fontSize: '11.5px', color: '#8e8ea8', marginTop: '2px' }}>
                  {seatsCombinedUsed} of {seatsAllowedLabel} seats used
                </div>
              )}
            </div>
            {isOrgOwner && (
              <button
                style={{ ...btn('primary'), fontSize: '12px', padding: '6px 12px', opacity: seatsFull ? 0.5 : 1, cursor: seatsFull ? 'not-allowed' : 'pointer' }}
                onClick={() => !seatsFull && setShowInviteModal(true)}
                title={seatsFull ? 'Seat limit reached for your plan' : undefined}
              >
                + Invite
              </button>
            )}
          </div>

          {isOrgOwner && seatsAllowed !== 'unlimited' && (
            <div style={{ height: '5px', borderRadius: '3px', background: '#1e1e27', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ width: `${seatsPct}%`, height: '100%', background: seatsFull ? '#f87171' : '#6366f1', transition: 'width .2s' }} />
            </div>
          )}

          {teamError && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.3)', borderRadius: '7px', padding: '8px 10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '11.5px', color: '#f87171' }}>{teamError}</span>
              <button onClick={() => setTeamError(null)} style={{ ...btn('ghost'), padding: '2px 6px', fontSize: '11px' }}>Dismiss</button>
            </div>
          )}

          {isTeamLoading ? (
            <div style={{ fontSize: '12.5px', color: '#52525f', padding: '12px 0', textAlign: 'center' as const }}>Loading team…</div>
          ) : (
            <>
              {pendingInvites.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#8e8ea8', marginBottom: '8px', textTransform: 'uppercase' as const, letterSpacing: '.04em' }}>
                    Pending Invites
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pendingInvites.map(invite => (
                      <div key={invite.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: '#17171e', border: '1px solid #26262f', borderRadius: '7px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', color: '#eeeef5', fontWeight: 600 }}>{invite.email || 'Shareable link'}</div>
                          <div style={{ fontSize: '10.5px', color: '#52525f' }}>
                            Expires {new Date(invite.expires_at).toLocaleDateString()}
                          </div>
                        </div>
                        {isOrgOwner && (
                          <>
                            <button style={{ ...btn('secondary'), fontSize: '11px', padding: '4px 9px' }} onClick={() => copyInviteLink(invite)}>
                              {copiedInviteId === invite.id ? 'Copied' : 'Copy Link'}
                            </button>
                            <button
                              style={{ ...btn('ghost'), fontSize: '11px', padding: '4px 9px', color: '#f87171' }}
                              onClick={() => revokeInvite(invite)}
                              disabled={pendingActionId === invite.id}
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#8e8ea8', marginBottom: '8px', textTransform: 'uppercase' as const, letterSpacing: '.04em' }}>
                Members
              </div>
              {teamMembers.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#52525f', padding: '8px 0' }}>No team members yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {teamMembers.map(member => (
                    <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: '#17171e', border: '1px solid #26262f', borderRadius: '7px' }}>
                      <div style={{
                        width: '26px', height: '26px', borderRadius: '50%',
                        background: 'linear-gradient(135deg,#6366f1,#a855f7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: 700, color: '#fff', flexShrink: 0,
                      }}>
                        {member.username.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#eeeef5' }}>{member.username}</div>
                        <div style={{ fontSize: '10.5px', color: '#52525f' }}>{member.email}</div>
                      </div>

                      {isOrgOwner && (
                        <>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', color: '#8e8ea8', cursor: 'pointer', opacity: pendingActionId === member.id ? 0.5 : 1 }}>
                            <span style={{ whiteSpace: 'nowrap' as const }}>Can add connections</span>
                            <span
                              onClick={() => toggleCanAddConnections(member)}
                              style={{
                                width: '28px', height: '16px', borderRadius: '9px', position: 'relative' as const,
                                background: member.can_add_connections ? '#6366f1' : '#26262f',
                                transition: 'background .15s', flexShrink: 0,
                              }}
                            >
                              <span style={{
                                position: 'absolute' as const, top: '2px',
                                left: member.can_add_connections ? '14px' : '2px',
                                width: '12px', height: '12px', borderRadius: '50%', background: '#fff',
                                transition: 'left .15s',
                              }} />
                            </span>
                          </label>
                          <button style={{ ...btn('secondary'), fontSize: '10.5px', padding: '4px 8px' }} onClick={() => setAccessModalMember(member)}>
                            AWS Access
                          </button>
                          <button
                            style={{ ...btn('ghost'), fontSize: '10.5px', padding: '4px 8px', color: '#f87171' }}
                            onClick={() => removeMember(member)}
                            disabled={pendingActionId === member.id}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* All plans */}
        {/* {allPlans.length > 0 && (
          <div style={{ ...card, marginBottom: '16px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '16px' }}>Available Plans</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(allPlans.length, 4)},1fr)`, gap: '10px' }}>
              {allPlans.map(plan => {
                const isCurrentPlan = currentPlan?.plan_id === plan.plan_id;
                const accent = planAccentColor(plan.plan_name);
                return (
                  <div
                    key={plan.plan_id}
                    style={{
                      background: isCurrentPlan ? accent.bg : '#17171e',
                      border: `1px solid ${isCurrentPlan ? accent.border : '#26262f'}`,
                      borderRadius: '8px', padding: '14px',
                      position: 'relative' as const,
                    }}
                  >
                    {isCurrentPlan && (
                      <div style={{ position: 'absolute' as const, top: '-10px', left: '50%', transform: 'translateX(-50%)', background: accent.color, color: '#fff', fontSize: '9.5px', fontWeight: 700, padding: '2px 9px', borderRadius: '10px', textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const }}>
                        Current
                      </div>
                    )}

                    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '14px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
                      {formatPlanName(plan.plan_name)}
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 800, fontFamily: "'Syne',sans-serif", color: accent.color, lineHeight: 1, marginBottom: '2px' }}>
                      {plan.deployments_allowed_per_month}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#52525f', marginBottom: '12px' }}>deployments/month</div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
                      {perkRow(plan.perks.monitoring, 'Monitoring')}
                      {perkRow(plan.perks.alerts, 'Alerts')}
                      {perkRow(plan.perks.team_based_access, 'Team Access')}
                      <div style={{ fontSize: '11.5px', color: '#8e8ea8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#52525f' }}>⏱</span> {plan.perks.email_response_time}h support
                      </div>
                    </div>

                    {isCurrentPlan ? (
                      <button disabled style={{ width: '100%', padding: '6px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, background: '#1e1e27', color: '#52525f', border: 'none', cursor: 'not-allowed' }}>
                        Current Plan ✓
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(plan)}
                        style={{ width: '100%', padding: '6px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer' }}
                      >
                        {plan.plan_name === 'free' ? 'Downgrade' : 'Upgrade'} →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )} */}

        {/* Repository connections */}
        <div style={{ ...card, marginTop: '16px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '14px' }}>Repository Connections</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* GitHub */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#17171e', border: '1px solid #26262f', borderRadius: '8px' }}>
              <Github size={20} color="#eeeef5" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>GitHub</div>
                <div style={{ fontSize: '11.5px', color: '#52525f' }}>{username} · connected</div>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600, background: 'rgba(34,197,94,.1)', color: '#22c55e' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#22c55e' }}/>
                Connected
              </span>
            </div>

            {/* Bitbucket — commented out until connected
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#17171e', border: '1px solid #26262f', borderRadius: '8px' }}>
              <BitbucketIcon size={20} color="#8e8ea8" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>Bitbucket</div>
                <div style={{ fontSize: '11.5px', color: '#52525f' }}>Not connected</div>
              </div>
              <button style={{ ...btn('secondary'), fontSize: '11.5px', padding: '4px 10px' }}>Connect</button>
            </div>
            */}

            {/* GitLab — commented out until connected
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#17171e', border: '1px solid #26262f', borderRadius: '8px' }}>
              <GitlabIcon size={20} color="#8e8ea8" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>GitLab</div>
                <div style={{ fontSize: '11.5px', color: '#52525f' }}>Not connected</div>
              </div>
              <button style={{ ...btn('secondary'), fontSize: '11.5px', padding: '4px 10px' }}>Connect</button>
            </div>
            */}

            

          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#eeeef5', marginBottom: '12px' }}>
            Available Plans
          </div>
          <div style={{
            margin: '0 -24px',           // cancel parent padding
            padding: '0 24px',
            overflowX: 'auto',           // allow horizontal scroll on narrow screens
          }}>
            <div style={{
              transform: 'none',
              // neutralize PlansPage's own max-width centering
              display: 'contents',
            }}>
              <PlansPage embedded />
            </div>
          </div>
        </div>
      </div>

      

      {showBillingModal && selectedPlan && (
        <BillingFormModal
          isOpen={showBillingModal}
          onClose={handleCloseBillingModal}
          planId={selectedPlan.plan_id}
          planName={formatPlanName(selectedPlan.plan_name)}
        />
      )}

      {showInviteModal && (
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvited={fetchTeamData}
          seatsUsed={seatsUsed}
          seatsAllowed={seatsAllowed}
          pendingInvitesCount={pendingInvites.length}
        />
      )}

      {accessModalMember && (
        <GrantAwsAccessModal
          isOpen={!!accessModalMember}
          member={accessModalMember}
          onClose={() => setAccessModalMember(null)}
        />
      )}
    </div>
  );
};